import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Input, Button, Alert } from '../components/design-system';
import { Mail, Lock, Truck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      navigate('/');
    } catch (err) {
      setError(err.message || t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="bg-white rounded-2xl shadow-xl p-6">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="h-16 w-16 bg-primary-500 rounded-2xl flex items-center justify-center shadow-lg shadow-primary-500/30">
              <Truck className="h-8 w-8 text-white" />
            </div>
          </div>

          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-xl font-bold text-gray-900 mb-1">{t('auth.welcomeBack')}</h1>
            <p className="text-sm text-gray-600">{t('auth.signInDescription')}</p>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert variant="error" className="mb-4" dismissible onDismiss={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label={t('auth.email')}
              type="email"
              placeholder="you@example.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              leftIcon={<Mail className="h-5 w-5 text-gray-400" />}
              autoComplete="email"
              required
              fullWidth
            />

            <Input
              label={t('auth.password')}
              type="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              leftIcon={<Lock className="h-5 w-5 text-gray-400" />}
              autoComplete="current-password"
              required
              fullWidth
            />

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2">
                <input type="checkbox" className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                <span className="text-gray-600">{t('auth.rememberMe')}</span>
              </label>
              <Link to="/forgot-password" className="text-primary-600 hover:text-primary-700">
                {t('auth.forgotPasswordLink')}
              </Link>
            </div>

            <Button type="submit" fullWidth loading={loading} size="lg">
              {t('auth.signIn')}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-6 text-center text-sm text-gray-600">
            {t('auth.dontHaveAccount')}{' '}
            <Link to="/register" className="text-primary-600 hover:text-primary-700 font-medium">
              {t('auth.signUp')}
            </Link>
          </div>
        </div>

        <p className="text-center text-xs text-gray-500 mt-4">
          {t('auth.protectedBy')}
        </p>
      </div>
    </div>
  );
};
